'use client';

import React from 'react';
import { Star } from 'lucide-react';
import {
  type Product,
  type DogSize,
  KIBBLE_TYPE_LABELS,
  AGE_RANGE_LABELS,
  getBestPrice,
  RETAILERS,
} from '@/lib/mockData';

const SIZE_BADGE: Record<DogSize, string> = {
  XS: 'bg-pink-100 text-pink-700',
  S: 'bg-yellow-100 text-yellow-700',
  M: 'bg-orange-100 text-orange-700',
  L: 'bg-teal-100 text-teal-700',
  XL: 'bg-blue-100 text-blue-700',
};

export default function ProductHeader({
  product,
  selectedWeight,
}: {
  product: Product;
  selectedWeight: number;
}) {
  const best = getBestPrice(product.id, selectedWeight);
  const bestRetailer = RETAILERS.find((r) => r.id === best?.retailerId);

  return (
    <div className="card p-5">
      {/* Image */}
      <div className="w-full h-52 rounded-xl overflow-hidden bg-gradient-to-br from-orange-50 to-amber-50 mb-4">
        <img
          src={product.imageUrl}
          alt={product.imageAlt}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Brand + name */}
      <p className="text-xs font-bold text-orange-500 uppercase tracking-wide mb-1">{product.brand}</p>
      <h1 className="font-display text-xl font-extrabold text-gray-900 leading-tight mb-3">
        {product.name}
      </h1>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {product.dogSizes.map((size) => (
          <span key={`header-size-${size}`} className={`badge-size ${SIZE_BADGE[size]}`}>
            {size}
          </span>
        ))}
        <span className="badge-size bg-amber-100 text-amber-700">
          {KIBBLE_TYPE_LABELS[product.kibbleType]}
        </span>
        <span className="badge-size bg-gray-100 text-gray-600">
          {AGE_RANGE_LABELS[product.ageRange]}
        </span>
      </div>

      {/* Rating */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={`header-star-${s}`}
              size={14}
              className={s <= Math.round(product.ratings) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
            />
          ))}
        </div>
        <span className="text-sm font-semibold text-gray-700">{product.ratings}</span>
        <span className="text-sm text-gray-400">({product.reviewCount.toLocaleString('fr-FR')} avis)</span>
      </div>

      {/* Best price callout */}
      {best && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
          <p className="text-xs text-gray-500 mb-1">Meilleur prix pour {selectedWeight} kg</p>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-extrabold text-orange-600 tabular-nums">
              {best.price.toFixed(2)} €
            </span>
          </div>
          <p className="text-sm text-gray-500 tabular-nums mt-0.5">
            soit <span className="font-semibold text-teal-700">{best.pricePerKg.toFixed(2)} €/kg</span>
          </p>
          {bestRetailer && (
            <p className="text-xs text-gray-400 mt-1.5">
              Chez{' '}
              <span className="font-bold" style={{ color: bestRetailer.logoColor }}>
                {bestRetailer.name}
              </span>
              {best.shippingCost === null ? (
                <span className="ml-1 text-green-600 font-semibold">· Livraison gratuite</span>
              ) : (
                <span className="ml-1">· +{best.shippingCost.toFixed(2)} € de livraison</span>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}